import React, { useCallback, useState } from 'react'
import {
  Box,
  Button,
  buttonClasses,
  ClickAwayListener,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { NavLink, useLocation } from 'react-router-dom'

import { CONTENT_WIDTH, CONTENT_WRAPPER_SX, DASHBOARD_MENU } from '../../constants.ts'
import { Icon } from '../../components'
import { useUser } from '@/stores/slices/user-slice.ts'

export const NavigationMenu = (): React.ReactElement | null => {
  const user = useUser()
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()

  const handleToggleMenu = useCallback(() => setMenuOpen((open) => !open), [])

  if (!user?.id) return null

  const userMenu = DASHBOARD_MENU(user.permissions)
  const activeMenu = userMenu.find((menu) => location.pathname.includes(menu.href))
  const renderButtonMenu = userMenu.length > 2

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 6 }}>
      <Box
        sx={{
          position: 'relative',
          ...CONTENT_WRAPPER_SX,
          mt: 0,
          zIndex: 2,
        }}
      >
        {renderButtonMenu ? (
          <>
            <Button
              variant="text"
              color="secondary"
              startIcon={<Icon name="Menu" />}
              endIcon={<Icon name="ArrowDown" />}
              onClick={handleToggleMenu}
              sx={({ transitions, palette }) => ({
                color: palette.text.primary,
                px: 0,
                [`& .${buttonClasses.endIcon} svg`]: {
                  transition: transitions.create('transform'),
                  transform: `rotate(${menuOpen ? '180deg' : '0deg'})`,
                },
              })}
            >
              <Typography variant="h3" color="text.primary">
                {' '}
                {activeMenu?.title}
              </Typography>
            </Button>
            {menuOpen && (
              <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
                <Box
                  sx={{
                    marginTop: 1,
                    backgroundColor: 'background.default',
                    boxShadow: 2,
                    maxWidth: 325,
                    width: 325,
                    overflow: 'hidden',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'lines.grey',
                    position: 'absolute',
                  }}
                >
                  <nav>
                    <List>
                      {userMenu.map(({ href, title }) => (
                        <ListItem disablePadding key={href}>
                          <ListItemButton component={NavLink} to={href} onClick={handleToggleMenu}>
                            <ListItemText primary={title} sx={{ my: 1 }} />
                            {activeMenu?.href === href && (
                              <ListItemIcon
                                sx={{
                                  color: 'text.primary',
                                  justifyContent: 'center',
                                }}
                              >
                                <Icon name="Check" />
                              </ListItemIcon>
                            )}
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </nav>
                </Box>
              </ClickAwayListener>
            )}
          </>
        ) : (
          <Stack
            margin="0 auto"
            columnGap={3}
            flexDirection="row"
            alignItems="center"
            height="100%"
            maxWidth={CONTENT_WIDTH}
          >
            {userMenu.map(({ href, title }) => (
              <Link
                key={href}
                component={NavLink}
                to={href}
                variant="h4"
                color="inherit"
                sx={({ transitions }) => ({
                  height: '100%',
                  py: 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'text.tertiary',
                  transition: transitions.create('color'),
                  '&:hover': {
                    color: 'text.primary',
                  },
                  '&.active': {
                    color: 'text.primary',
                    '&::before': {
                      content: '""',
                      display: 'block',
                      //width: '100%',
                      height: 2,
                      backgroundColor: 'background.greenHover',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      borderRadius: 2,
                    },
                  },
                })}
              >
                {title}
              </Link>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  )
}
